import {
  Modal,
  NewButton,
  NewButtonVariant,
  SlidingPanel,
} from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { MessageSquare } from "lucide-react";
import { func, number, object, oneOf, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteComment,
  editComment,
  fetchDiscussion,
  postComment,
  selectDiscussion,
} from "slices/comments";

import CommentComposer from "./CommentComposer";
import CommentListItem from "./CommentListItem";
import classes from "./CommentThreadPanel.module.scss";

const SURFACES = [
  "SHRED_PARAGRAPH",
  "SHRED_SENTENCE",
  "ACRONYM_ENTRY",
  "COMPARE_RESULT",
  "OUTLINE_SECTION",
];

const commentCountLabel = (commentCount) =>
  `${commentCount} comment${commentCount === 1 ? "" : "s"}`;

const CommentThreadPanel = ({
  surface,
  containerId,
  itemRef,
  itemRefKey,
  triggerRef,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { itemLabel, commentCount, threads, isLoading } = useSelector(
    selectDiscussion(surface, containerId, itemRef)
  );

  const [newCommentBody, setNewCommentBody] = useState("");
  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [pendingDeleteComment, setPendingDeleteComment] = useState(null);

  useEffect(() => {
    dispatch(fetchDiscussion({ surface, containerId, itemRef }));
  }, [surface, containerId, itemRefKey]);

  const handleClose = () => {
    onClose();
    triggerRef?.current?.focus();
  };

  const handleCancelNewComment = () => {
    setNewCommentBody("");
    handleClose();
  };

  const handleSubmitNewComment = async () => {
    await dispatch(
      postComment({
        surface,
        containerId,
        itemRef,
        parentCommentId: null,
        body: newCommentBody,
      })
    ).unwrap();
    setNewCommentBody("");
  };

  const handleStartReply = (commentId) => {
    setReplyingCommentId(commentId);
    setReplyBody("");
  };

  const handleCancelReply = () => {
    setReplyingCommentId(null);
    setReplyBody("");
  };

  const handleSubmitReply = async () => {
    await dispatch(
      postComment({
        surface,
        containerId,
        itemRef,
        parentCommentId: replyingCommentId,
        body: replyBody,
      })
    ).unwrap();
    setReplyingCommentId(null);
    setReplyBody("");
  };

  const handleStartEdit = (comment) => {
    setEditingCommentId(comment.id);
    setEditBody(comment.body ?? "");
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditBody("");
  };

  const handleSubmitEdit = async () => {
    await dispatch(
      editComment({
        surface,
        containerId,
        itemRef,
        commentId: editingCommentId,
        body: editBody,
      })
    ).unwrap();
    setEditingCommentId(null);
    setEditBody("");
  };

  const handleDelete = (comment) => {
    setPendingDeleteComment(comment);
  };

  const handleCancelDelete = () => {
    setPendingDeleteComment(null);
  };

  const handleConfirmDelete = async () => {
    await dispatch(
      deleteComment({
        surface,
        containerId,
        itemRef,
        commentId: pendingDeleteComment.id,
      })
    ).unwrap();
    dispatch(fetchDiscussion({ surface, containerId, itemRef }));
    setPendingDeleteComment(null);
  };

  const replyState = {
    commentId: replyingCommentId,
    body: replyBody,
    onChange: setReplyBody,
    onSubmit: handleSubmitReply,
    onCancel: handleCancelReply,
  };

  const editState = {
    commentId: editingCommentId,
    body: editBody,
    onChange: setEditBody,
    onSubmit: handleSubmitEdit,
    onCancel: handleCancelEdit,
  };

  return (
    <>
      <SlidingPanel>
        <SlidingPanel.Header onHide={handleClose}>
          <Flex gap={GAP_CLASS.GAP_200}>
            <MessageSquare
              size={18}
              className={classes["header__icon"]}
              aria-hidden="true"
            />
            Comments
          </Flex>
        </SlidingPanel.Header>
        <SlidingPanel.SubHeader className={classes["subheader"]}>
          {itemLabel} · {commentCountLabel(commentCount)}
        </SlidingPanel.SubHeader>
        <SlidingPanel.Body>
          {isLoading && threads.length === 0 ? (
            <Flex
              justifyContent="center"
              className={classes["loading"]}
              data-testid="comments-loading"
            >
              Loading…
            </Flex>
          ) : commentCount === 0 ? (
            <p className={classes["empty"]}>
              No comments yet. Be the first to leave a note on this section.
            </p>
          ) : (
            <Flex
              direction={DIRECTION_CLASS.column}
              alignItems={ALIGN_CLASS.stretch}
              gap={GAP_CLASS.GAP_400}
            >
              {threads.map((thread) => (
                <div key={thread.comment.id} className={classes["thread"]}>
                  <CommentListItem
                    thread={thread}
                    replyState={replyState}
                    editState={editState}
                    onStartReply={handleStartReply}
                    onStartEdit={handleStartEdit}
                    onDelete={handleDelete}
                  />
                </div>
              ))}
            </Flex>
          )}
        </SlidingPanel.Body>
        <SlidingPanel.Footer>
          <CommentComposer
            value={newCommentBody}
            onChange={setNewCommentBody}
            onSubmit={handleSubmitNewComment}
            onCancel={handleCancelNewComment}
            placeholder="Add a comment on this section…"
            submitLabel="Comment"
          />
        </SlidingPanel.Footer>
      </SlidingPanel>
      {pendingDeleteComment && (
        <Modal
          show
          onDismiss={handleCancelDelete}
          backdrop
          centered
          className={classes["delete_modal"]}
          backdropClassName={classes["delete_modal_backdrop"]}
        >
          <Modal.Header>Delete comment?</Modal.Header>
          <Modal.Body>
            <p>This comment and all its replies will be permanently removed.</p>
          </Modal.Body>
          <Modal.Footer>
            <NewButton
              variant={NewButtonVariant.l2}
              onClick={handleCancelDelete}
            >
              Cancel
            </NewButton>
            <NewButton
              variant={NewButtonVariant.l1}
              onClick={handleConfirmDelete}
            >
              Delete comment
            </NewButton>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

CommentThreadPanel.propTypes = {
  surface: oneOf(SURFACES).isRequired,
  containerId: number.isRequired,
  itemRef: object.isRequired,
  itemRefKey: string.isRequired,
  triggerRef: shape({ current: object }),
  onClose: func.isRequired,
};

export default CommentThreadPanel;
